import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { SearchUpdateEvent } from '@shopify/events';

/**
 * Compact predictive search: opens only after 2+ typed characters.
 * Never shows recently viewed, and never opens on focus or after navigation.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput
 * @property {HTMLElement} predictiveSearchResults
 * @property {HTMLElement} resetButton
 * @property {HTMLElement[]} [resultsItems]
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  /**
   * @returns {DialogComponent | null}
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    this.#closePanel();

    document.addEventListener('pointerdown', this.#handlePointerDown, { signal });
    document.addEventListener('keydown', this.#handleDocumentKeyDown, { signal });
    window.addEventListener('pagehide', this.#closePanel, { signal });
    window.addEventListener('pageshow', this.#handlePageShow, { signal });

    this.refs.searchInput.addEventListener('focus', this.#handleFocus, { signal });
    this.refs.predictiveSearchResults.addEventListener('click', this.#handleResultClick, { signal });
    this.querySelector('form')?.addEventListener('submit', this.#handleSubmit, { signal });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * @param {MouseEvent} event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.refs.searchInput.value = '';
    this.#hideResetButton();
    this.#closePanel();
  };

  #handleDialogOpen = () => {
    this.#closePanel();
  };

  #handleFocus = () => {
    if (this.refs.searchInput.value.trim().length < 2) {
      this.#closePanel();
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (!this.#isOpen()) return;
    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;
    this.#closePanel();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleDocumentKeyDown = (event) => {
    if (event.key === 'Escape' && this.#isOpen()) {
      this.#closePanel();
    }
  };

  #handlePageShow = () => {
    this.#closePanel();
  };

  #handleSubmit = () => {
    this.#closePanel();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleResultClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.closest('a')) {
      this.#closePanel();
    }
  };

  #isOpen() {
    return this.classList.contains('is-open');
  }

  get #allResultsItems() {
    const items = Array.from(this.refs.predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"], .ps-results__item'));
    return /** @type {HTMLElement[]} */ (items.filter((item) => item instanceof HTMLElement));
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    let activeItem = null;

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  /**
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closePanel();
      return;
    }

    if (!this.#allResultsItems.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (this.#isOpen()) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = event.shiftKey
            ? currentIndex > 0
              ? currentIndex - 1
              : totalItems - 1
            : currentIndex < totalItems - 1
              ? currentIndex + 1
              : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#closePanel();
          this.#currentItem?.querySelector('a')?.click();
          if (this.#currentItem instanceof HTMLAnchorElement) {
            this.#currentItem.click();
          }
        } else {
          this.#closePanel();
        }
        break;
      }
    }
  };

  /**
   * Clear button: empty the field and close the panel.
   * @param {boolean} [keepFocus=true]
   */
  resetSearch = debounce((keepFocus = true) => {
    this.refs.searchInput.value = '';
    this.#hideResetButton();
    this.#closePanel();
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
  }, 100);

  /**
   * @param {InputEvent} event
   */
  search = debounce((_event) => {
    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (searchTerm.length > 0) {
      this.#showResetButton();
    } else {
      this.#hideResetButton();
    }

    if (searchTerm.length < 2) {
      this.#closePanel();
      return;
    }

    this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.scrollTo(0, 0);
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
    });
  }

  /**
   * @param {string} searchTerm
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '6');
    url.searchParams.set('resources[limit_scope]', 'each');

    const abortController = this.#createAbortController();
    const deferredPromise = SearchUpdateEvent.createPromise();

    this.dispatchEvent(
      new SearchUpdateEvent({
        search: {
          query: searchTerm,
        },
        promise: deferredPromise.promise,
      })
    );

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (abortController.signal.aborted) {
          deferredPromise.reject(new Error('Fetch aborted by user'));
          return;
        }

        if (!resultsMarkup) {
          this.#closePanel();
          deferredPromise.resolve({ totalCount: 0 });
          return;
        }

        this.#renderResults(resultsMarkup);
        const resultCount = this.refs.predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]').length;
        deferredPromise.resolve({ totalCount: resultCount });
      })
      .catch((error) => {
        deferredPromise.reject(error);
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * @param {string} resultsMarkup
   */
  #renderResults(resultsMarkup) {
    const parsed = new DOMParser().parseFromString(resultsMarkup, 'text/html');
    const dropdown =
      parsed.querySelector('.predictive-search-dropdown') ||
      parsed.querySelector('#predictive-search-results') ||
      parsed.body.firstElementChild;

    const { predictiveSearchResults } = this.refs;
    predictiveSearchResults.replaceChildren();

    if (!dropdown || !dropdown.querySelector('.ps-results, .ps-empty, .ps-view-all')) {
      this.#closePanel();
      return;
    }

    predictiveSearchResults.appendChild(document.importNode(dropdown, true));
    this.#openPanel();
    this.#resetScrollPositions();
  }

  #openPanel() {
    const { predictiveSearchResults, searchInput } = this.refs;
    predictiveSearchResults.hidden = false;
    this.classList.add('is-open');
    searchInput.setAttribute('aria-expanded', 'true');
  }

  #closePanel = () => {
    this.#currentIndex = -1;
    this.#activeFetch?.abort();
    this.#activeFetch = null;

    const { predictiveSearchResults, searchInput } = this.refs;
    if (predictiveSearchResults) {
      predictiveSearchResults.replaceChildren();
      predictiveSearchResults.hidden = true;
    }

    this.classList.remove('is-open');
    searchInput?.setAttribute('aria-expanded', 'false');
  };

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
